import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
    Box,
    Container,
    Typography,
    Divider,
    TextField,
    Autocomplete,
    Chip,
    InputAdornment,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    IconButton,
} from "@mui/material";
import { ExpandMore } from "@mui/icons-material";
import queryGraphql from "../../helpers/ApiInterface";
import MainDrawer from "../MainDrawer";
import APIRequestsTable from "./APIRequestsTable";
import MiniBot from "../MiniBot";

interface SearchOptions {
    chipLabel: string;
    dropdownLabel: string;
    category: string;
    groupBy: string;
}

let searchOptions: SearchOptions[] = [];

const APIRequestsPage: React.FC = () => {
    const [apis, setAPIs] = useState<any[]>([]);
    const [searchQuery, setSearchQuery] = useState<SearchOptions[]>([]);
    const [updates, setUpdates] = useState<any[]>([]);
    
    useEffect(() => {
        const fetchStatuses = async () => {
            try {
                const data = await queryGraphql(`
                    query Query {
                      resourceStatuses {
                        id
                        currentStatus
                        resourceId
                      }
                    }`);
                if (data.resourceStatuses) {
                    setUpdates(data.resourceStatuses);
                }
            } catch (e) {
                console.log(e);
            }
        };
        fetchStatuses();
    }, []);  
    
    useEffect(() => {
        const fetchData = async () => {
            try {
                const data = await queryGraphql(`
                    query GetAPIBackends {
                      getAPIBackends {
                        id
                        name
                        cloudProvider
                        environment
                        status
                        apiBackendOwner {
                          id
                          name
                          googleProfilePicture
                        }
                        apiBackendApprover {
                          id
                          name
                          googleProfilePicture
                        }
                        application {
                          applicationName
                          id
                        }
                        service {
                          id
                          serviceName
                        }
                        reasonForRejection
                        createdAt
                        updatedAt
                        path
                        httpMethod
                        apiVersion
                        serviceType
                      }
                    }`);

                const rawAPIData = data.data?.getAPIBackends || data.getAPIBackends || [];

                let apisArr = rawAPIData.map((e: any) => ({
                    id: e.id,
                    apiName: e.name,
                    applicationName: e.application?.applicationName,
                    serviceName: e.service?.serviceName,
                    status: e.status ? e.status.toLowerCase() : 'pending',
                    createdAt: e.createdAt ? new Date(Number(e.createdAt)).toISOString() : null,
                    actionedAt: e.updatedAt ? new Date(Number(e.updatedAt)).toISOString() : null,
                    apiOwnerName: e.apiBackendOwner?.name,
                    apiOwnerAvatar: e.apiBackendOwner?.googleProfilePicture, // Note: googleProfilePicture is NOT in the new query, so this will correctly resolve to 'undefined'.
                    cloudProvider: e.cloudProvider,
                    path: e.path, // Use flat property
                    httpMethod: e.httpMethod, // Use flat property
                    apiVersion: e.apiVersion, // Use flat property
                    apiApproverName: e.apiBackendApprover?.name,
                    reasonForRejection: e.reasonForRejection,
                    apiApproverAvatar: e.apiBackendApprover?.googleProfilePicture, 
                }));
                setAPIs(apisArr);

                searchOptions = apisArr
                    .map((e: any) => {
                        const options: SearchOptions[] = [
                            { category: "apiName", groupBy: "API BACKEND NAME", chipLabel: `API Backend Name: ${e.apiName}`, dropdownLabel: e.apiName },
                            { category: "apiOwnerName", groupBy: "OWNER", chipLabel: `Owner: ${e.apiOwnerName}`, dropdownLabel: e.apiOwnerName },
                        ];
                        if (e.applicationName) options.push({ category: "applicationName", groupBy: "APPLICATION", chipLabel: `App: ${e.applicationName}`, dropdownLabel: e.applicationName });
                        if (e.serviceName) options.push({ category: "serviceName", groupBy: "SERVICE", chipLabel: `Service: ${e.serviceName}`, dropdownLabel: e.serviceName });
                        if (e.cloudProvider) options.push({ category: "cloudProvider", groupBy: "CLOUD PROVIDER", chipLabel: `Provider: ${e.cloudProvider}`, dropdownLabel: e.cloudProvider });
                        if (e.status) options.push({ category: "status", groupBy: "STATUS", chipLabel: `Status: ${e.status}`, dropdownLabel: e.status });
                        return options;
                    })
                    .flat();

                searchOptions = Array.from(new Map(searchOptions.map(item => [`${item.category}:${item.dropdownLabel}`, item])).values());
                searchOptions.sort((a, b) => a.groupBy.localeCompare(b.groupBy) || a.dropdownLabel.localeCompare(b.dropdownLabel));

            } catch (error) {
                console.log(error);
            }
        };

        fetchData();
    }, []);

    const filteredAPIs =
        searchQuery.length === 0
            ? apis
            : apis.filter((api: any) =>
                searchQuery.every(filter => {
                    const apiValue = api[filter.category]?.toLowerCase();
                    const filterValue = filter.dropdownLabel?.toLowerCase();
                    return searchQuery.filter(f => f.category === filter.category).map(f => f.dropdownLabel.toLowerCase()).includes(apiValue);
                })
            );
    
    const filteredActiveAPIs = filteredAPIs.filter(api => ["active", "approved"].includes(api.status));
    const filteredPendingAPIs = filteredAPIs.filter(api => api.status === "pending");
    const filteredRejectedAPIs = filteredAPIs.filter(api => api.status === "rejected");

    return (
        <Box sx={{ display: "flex" }}>
            <MainDrawer index={"requests-apis"} />
            <MiniBot/>
            <Box component="main" sx={{ flex: 1, bgcolor: "#fafafa", minHeight: window.innerHeight }}>
                <Container maxWidth={false} disableGutters>
                    <Box bgcolor="#FFFFFF" p={2} mb="18px">
                        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", my: 1, mx: 2 }}>
                            <Typography fontSize="22px" gutterBottom fontFamily="Gilroy-Bold">Requests</Typography>
                            <IconButton aria-label="Help" onClick={() => window.open(`${process.env.REACT_APP_HOST_DOMAIN}/docs`, "_blank")} sx={{ p: 0, border: 'none', mr:2 }}>
                                <Box component="img" src="/assets/Help.svg" alt="Help Icon" sx={{ width: 40, height: 40 }}/>
                            </IconButton>
                        </Box>
                        <Divider sx={{ my: 1 }} />
                        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mx: 2 }}>
                        <Box>
                            <Typography fontFamily="Gilroy-SemiBold" fontSize="16px">{`APIs Backend (${apis.length})`}</Typography>
                            <Typography fontFamily="Gilroy-Regular" fontSize="14px">{`View all the requests raised for APIs Backend`}</Typography>
                        </Box>
                        <Autocomplete
                            multiple
                            options={searchOptions}
                            filterSelectedOptions
                            getOptionLabel={(option) => option.dropdownLabel}
                            groupBy={(option) => option.groupBy}
                            renderTags={(tagValue, getTagProps) =>
                                tagValue.map((option, index) => <Chip {...getTagProps({ index })} size="small" label={option.chipLabel} />)
                            }
                            onChange={(_, newVal) => setSearchQuery(newVal)}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    variant="filled"
                                    placeholder="Search by API Backend name, owner, status etc"
                                    // 1. ADDED THESE PROPS
                                    fullWidth
                                    size="small"
                                    hiddenLabel
                                    InputProps={{
                                        ...params.InputProps,
                                        startAdornment: (
                                            <>
                                                <InputAdornment position="start">
                                                    <img src="/assets/search-normal.svg" alt="Search" style={{ width: "16px", paddingLeft: "4px" }}/>
                                                </InputAdornment>
                                                {params.InputProps.startAdornment}
                                            </>
                                        ),
                                        disableUnderline: true,
                                        // 2. ADDED FONT STYLES
                                        style: {
                                            backgroundColor: "#f4f4f4",
                                            minHeight: "45px",
                                            width: "500px",
                                            borderRadius: "8px",
                                            fontFamily: "Gilroy-Regular",
                                            fontSize: "14px",
                                        },
                                    }}
                                />
                            )}
                        />
                    </Box>
                    </Box>
                    <Box m={2}>
                        <Accordion defaultExpanded={filteredPendingAPIs.length > 0} elevation={0} sx={{ borderRadius: "8px", mb: 2, '&:before': { display: 'none' }, bgcolor: "#fafafa" }} disabled={filteredPendingAPIs.length === 0}>
                            <AccordionSummary expandIcon={<ExpandMore />}><Box sx={{ display: "flex", alignItems: "center" }}><img src="/assets/clock.svg" alt="Pending" style={{ width: "20px", marginRight: "8px" }} /><Typography fontSize="16px" fontFamily="Gilroy-SemiBold">Pending APIs Backend({filteredPendingAPIs.length})</Typography></Box></AccordionSummary>
                            <AccordionDetails><APIRequestsTable apis={filteredPendingAPIs} resourceStatuses={updates} /></AccordionDetails>
                        </Accordion>
                        <Accordion defaultExpanded={filteredActiveAPIs.length > 0} elevation={0} sx={{ borderRadius: "8px", mb: 2, '&:before': { display: 'none' }, bgcolor: "#fafafa" }} disabled={filteredActiveAPIs.length === 0}>
                            <AccordionSummary expandIcon={<ExpandMore />}><Box sx={{ display: "flex", alignItems: "center" }}><img src="/assets/tickcircle.svg" alt="Active" style={{ width: "20px", marginRight: "8px" }} /><Typography fontSize="16px" fontFamily="Gilroy-SemiBold">Active APIs Backend({filteredActiveAPIs.length})</Typography></Box></AccordionSummary>
                            <AccordionDetails><APIRequestsTable apis={filteredActiveAPIs} resourceStatuses={updates} /></AccordionDetails>
                        </Accordion>
                        <Accordion defaultExpanded={filteredRejectedAPIs.length > 0} elevation={0} sx={{ borderRadius: "8px", '&:before': { display: 'none' }, bgcolor: "#fafafa" }} disabled={filteredRejectedAPIs.length === 0}>
                            <AccordionSummary expandIcon={<ExpandMore />}><Box sx={{ display: "flex", alignItems: "center" }}><img src="/assets/reject.svg" alt="Rejected" style={{ width: "20px", marginRight: "8px" }} /><Typography fontSize="16px" fontFamily="Gilroy-SemiBold">Rejected APIs Backend({filteredRejectedAPIs.length})</Typography></Box></AccordionSummary>
                            <AccordionDetails><APIRequestsTable apis={filteredRejectedAPIs} resourceStatuses={updates} /></AccordionDetails>
                        </Accordion>
                    </Box>
                </Container>
            </Box>
        </Box>
    );
};

export default APIRequestsPage;